import React, { useCallback, useEffect } from "react"
import { observer } from "mobx-react-lite"
import useNavigation from "~hooks/useNavigation"
import { ScrollView } from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import SwitchGroup from "./components/SwitchGroup"
import { SettingScreenProps, SettingGroup, SettingItem } from "./types"
import { securitySchema } from "./schemas/security"
import { privacySchema } from "./schemas/privacy"
import { generalSchema } from "./schemas/general"
import { messageSchema } from "./schemas/message"
import { chatSchema } from "./schemas/chat"
import { useStores } from "~models"
import type { SettingStore } from "~models/setting-store"
import type { WalletStore } from "~models/wallet-store"
import type { BlackListStore } from "~models/black-list-store"
import { useModal } from "~hooks/useModal"
import { useWebview } from '~components/WebviewContext'
import { useWebviewClosedRefresh } from "~hooks/useWebviewClosedRefresh"
import { globalToast } from '~utils'

const getSettingSchema = ({
  type,
  settingStore,
  walletStore,
  blackListStore
}: {
  type: string | undefined
  settingStore: SettingStore
  walletStore: WalletStore
  blackListStore: BlackListStore
}): SettingGroup[] => {
  switch (type) {
    case "security":
      return securitySchema({ settingStore, walletStore })
    case "privacy":
      return privacySchema({ settingStore, blackListStore, walletStore })
    case "message":
      return messageSchema({ settingStore, walletStore })
    case "general":
      return generalSchema({ settingStore })
    case "chat":
      return chatSchema({ settingStore, walletStore })
    default:
      return []
  }
}

const Setting: React.FC<SettingScreenProps> = ({ route }) => {
  const navigation = useNavigation()
  const { Dialog } = useModal()
  const pageType = route.params?.type
  const { settingStore, eventStore, walletStore, blackListStore, userStore } = useStores()
  const { openWebview, clearWebview } = useWebview()
  const insets = useSafeAreaInsets()
  const currentUser = userStore.currentUser

  const onPressWebview = useCallback((path: string) => {
    openWebview({ path })
  }, [openWebview])

  const refreshAfterWebview = useCallback(() => {
    void walletStore.getWalletInfo()
    void walletStore.getMessageDestroySetting()
  }, [walletStore])

  useWebviewClosedRefresh(refreshAfterWebview)

  const onItemClick = useCallback(async (item: SettingItem) => {
    // console.log("item", item)
    switch (item.type) {
      case "navigate":
        if (item.id === "security.password") {
          onPressWebview("/user/update-pwd")
        } else if (item.id === "security.verification") {
          onPressWebview("/user/set-wp")
        } else if (item.id === "security.account") {
          onPressWebview("/user/phone")
        } else if (item.id === "security.email") {
          onPressWebview("/user/email")
        } else if (item.id === "security.security") {
          navigation.navigate('SecurityGoogle')
        } else if (item.id === "chat.groupAssistant") {
          navigation.navigate('GroupAssistant')
        } else if (item.id === "message.doNotDisturbTime") {
          navigation.navigate("NotDisturbMode")
        } else if (item.id === "chat.settingTimer") {
          navigation.navigate("SelfDestructTimeSettings")
        } else if (item.id === "message.notificationBell") {
          navigation.navigate("NotificationBell")
        } else if (item.id === "general.loginDevice") {
          navigation.navigate("LoginDevice")
          // globalToast.show('功能开发中,敬请期待!')
        } else if (item.id === "general.language") {
          // navigation.navigate("LanguageSettings")
          globalToast.show('功能开发中,敬请期待!')
        }else if (item.id === "general.fontSize") {
          // navigation.navigate("FontsizeSettings")
          globalToast.show('功能开发中,敬请期待!')
        } else if (item.id === "privacy.timeOnline") {
          navigation.navigate("OnlineTimeSettings")
        } else if (item.id === "privacy.addMethod") {
          navigation.navigate("AddMethodSettings")
        } else if (item.id === "privacy.blacklist") {
          navigation.navigate("BlackList")
        } else if (item.id === "general.clearData") {
          navigation.navigate("ClearCacheData")
        } else {
          globalToast.show('功能开发中,敬请期待!')
        }
        break
      case "switch":
        break
      case "info":
        // 只读展示项（如版本号），点击无动作
        break
      case "link":
        // showWebview(item.id)
        globalToast.show('功能开发中,敬请期待!')
        break
      case "action":
        // item.onClick()
        globalToast.show('功能开发中,敬请期待!')
        break
      case "danger":
        if (item.id === "general.logout") {
          Dialog.show({
            title: "退出登录",
            content: "确定要退出登录吗？退出后需要重新登录才能使用应用。",
            loadingText: "退出中...",
            onConfirm: async () => {
              clearWebview()
              await eventStore.publicLogout()
            },
          })
        } else {
          globalToast.show('功能开发中,敬请期待!')
        }
        break
      default:
        globalToast.show('功能开发中,敬请期待!')
        break
    }
  }, [navigation, Dialog, onPressWebview, clearWebview, eventStore])

  const columns = getSettingSchema({ type: pageType, settingStore, walletStore, blackListStore })

  useEffect(() => {
    if (currentUser) {
      blackListStore.queryBlacklistList(currentUser.id)
      walletStore.getMessageDestroySetting()
    }
  }, [currentUser])
  return (
    <ScrollView
      style={{ flex: 1, minHeight: 0 }}
      contentContainerStyle={{
        flexGrow: 0,
        paddingTop: 10,
        paddingLeft: 12,
        paddingRight: 12,
        paddingBottom: insets.bottom + 10,
      }}
    >
      {columns.map((item, index) => (
        <SwitchGroup
          item={item}
          key={index}
          onClick={onItemClick}
        />
      ))}
    </ScrollView>
  )
}

export default observer(Setting)
